import { getMaterialResourceUrl, type MaterialGoods, type MaterialResource } from '@/material/web/api'
import { useAuthStore } from '@/features/auth'
import {
  appendMaterialResourceMapping,
  appendMaterialResourceMappingEntry,
  findMaterialResourceMappingByCompositeSignature,
  findMaterialResourceMappingBySourceUrl,
} from '@/features/material-library/resourceMapping'
import { resolveMaterialApplyTarget } from '@/features/material-library/targets'
import {
  buildProjectResourceObjectName,
  uploadMaterialFile,
  type MaterialUploadProgressStage,
} from '@/features/material-library/upload'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import { useWorkspaceStore } from '@/store/workspace'

export type MaterialApplyItemResult = {
  fileName: string
  url: string
}

export type MaterialApplyResult = {
  fileName: string
  url: string
  category: ResourceCategoryKey
  folderSegments: string[]
  files: MaterialApplyItemResult[]
}

const MATERIAL_COMPOSITE_SIGNATURE_LINK_INDEX = 3

/** 取资源 `extra` 的末级文件名，屏蔽目录分隔符差异。 */
function trimPathName(name: string): string {
  const normalized = name.replace(/\\/g, '/').split('/').pop()?.trim() || ''
  return normalized || '素材'
}

/** 在素材原始文件名缺失扩展名时，根据下载后的 Blob 类型补齐扩展名。 */
function inferExtension(resource: MaterialResource, blob: Blob): string {
  const fileName = trimPathName(resource.extra)
  const match = /\.[^.]+$/.exec(fileName)
  if (match?.[0]) return match[0]
  const mime = blob.type.toLowerCase()
  if (mime.includes('png')) return '.png'
  if (mime.includes('jpeg')) return '.jpg'
  if (mime.includes('gif')) return '.gif'
  if (mime.includes('webp')) return '.webp'
  if (mime.includes('mpeg') || mime.includes('mp3')) return '.mp3'
  if (mime.includes('wav')) return '.wav'
  if (mime.includes('ogg')) return '.ogg'
  return ''
}

/** 为即将上传的素材构造最终文件名。 */
function buildTargetFileName(resource: MaterialResource, goods: MaterialGoods, blob: Blob): string {
  const rawName = trimPathName(resource.extra)
  if (/\.[^.]+$/.test(rawName)) return rawName
  const goodsName = goods.name.trim().replace(/[\\/:*?"<>|]/g, '_') || '素材'
  return `${goodsName}${inferExtension(resource, blob)}`
}

/** 下载素材原始文件，供后续上传到项目资源库。 */
async function downloadResourceBlob(resource: MaterialResource): Promise<Blob> {
  const url = getMaterialSourceDownloadUrl(resource)
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`素材下载失败：HTTP ${response.status}`)
  }
  return await response.blob()
}

/** 将素材资源统一映射为素材库原始下载地址。 */
export function getMaterialSourceDownloadUrl(resource: Pick<MaterialResource, 'resmd5'>): string {
  return resource.resmd5?.trim() ? getMaterialResourceUrl(resource.resmd5.trim()) : ''
}

/** 为当前立绘组合生成稳定签名，用于“已使用”判定与去重。 */
export function buildDollCompositeSignature(params: {
  goodsId: string
  size: string
  resources: Array<Pick<MaterialResource, 'resmd5'>>
}): string {
  const parts = params.resources
    .map((item) => item.resmd5?.trim() ?? '')
    .filter(Boolean)
  return parts.length ? `doll:${params.goodsId.trim()}:${params.size.trim() || '540'}:${parts.join('|')}` : ''
}

/** 按 `resmd5` 去重，避免同一资源重复下载和重复写入资源树。 */
function uniqueMaterialResources(resources: MaterialResource[]): MaterialResource[] {
  const seen = new Set<string>()
  const list: MaterialResource[] = []
  for (const resource of resources) {
    const key = resource.resmd5?.trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    list.push(resource)
  }
  return list
}

/** 校验登录态、当前工程和目标分类，返回应用流程所需上下文。 */
async function resolveApplyContext(topCategoryId: string) {
  const auth = useAuthStore()
  const workspace = useWorkspaceStore()
  const project = workspace.project
  if (!auth.loggedIn || !auth.sessionId || !auth.uid) {
    throw new Error('请先登录后再应用素材')
  }
  if (!project) {
    throw new Error('当前没有已打开工程')
  }

  const guid = project.projectHeader.ID?.trim()
  if (!guid) {
    throw new Error('当前工程缺少 guid，暂时无法应用素材到云端资源库')
  }

  return {
    auth,
    workspace,
    project,
    guid,
    target: resolveMaterialApplyTarget(topCategoryId),
  }
}

/**
 * 批量应用素材到当前工程的虚拟资源树。
 * 适用于立绘、背景、CG、音频等会同时导入多个资源文件的场景。
 */
export async function applyMaterialResourcesToWorkspace(params: {
  topCategoryId: string
  goods: MaterialGoods
  resources: MaterialResource[]
  onProgress?: (stage: MaterialUploadProgressStage, percent: number) => void
}): Promise<MaterialApplyResult> {
  const resources = uniqueMaterialResources(params.resources)
  if (!resources.length) {
    throw new Error('当前没有可应用的素材资源')
  }

  for (const resource of resources) {
    if (!resource.resmd5) {
      throw new Error('当前素材缺少资源标识，无法应用')
    }
  }

  const { auth, workspace, project, guid, target } = await resolveApplyContext(params.topCategoryId)
  const version = String(project.projectHeader.Version || project.projectConstant.gameVersion || 1)
  const uploadedFiles: MaterialApplyItemResult[] = []
  let nextRoot = workspace.virtualResourceRoots[target.category]
  let revealFolderSegments = [...target.folderSegments]

  for (const resource of resources) {
    const sourceUrl = getMaterialSourceDownloadUrl(resource)
    const existing = findMaterialResourceMappingBySourceUrl(nextRoot, sourceUrl)
    if (existing) {
      revealFolderSegments = existing.folderSegments.length ? existing.folderSegments : revealFolderSegments
      uploadedFiles.push({
        fileName: existing.node.name,
        url: existing.node.links[0] ?? '',
      })
      continue
    }

    let blob: Blob | null = null
    let file: File | null = null
    try {
      blob = await downloadResourceBlob(resource)
      const fileName = buildTargetFileName(resource, params.goods, blob)
      file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' })
      const objectName = buildProjectResourceObjectName({
        guid,
        category: target.category,
        folderSegments: target.folderSegments,
        fileName,
      })
      const uploaded = await uploadMaterialFile({
        file,
        guid,
        sessionId: auth.sessionId,
        version,
        objectName,
        resultFileName: fileName,
        onProgress: params.onProgress,
      })
      uploadedFiles.push({
        fileName: uploaded.fileName,
        url: uploaded.url,
      })

      nextRoot = appendMaterialResourceMapping({
        root: nextRoot,
        category: target.category,
        folderSegments: target.folderSegments,
        fileName: uploaded.fileName,
        url: uploaded.url,
        sourceUrl,
        md5: uploaded.md5,
        size: uploaded.size,
      })
      revealFolderSegments = [...target.folderSegments]
    } finally {
      blob = null
      file = null
    }
  }

  workspace.setVirtualResourceRoot(target.category, nextRoot)
  await workspace.uploadVirtualResourcesToCloud()
  workspace.revealResourceBrowserFolder(target.category, revealFolderSegments)
  const firstFile = uploadedFiles[0]
  if (!firstFile) {
    throw new Error('素材应用失败：未生成上传结果')
  }

  return {
    fileName: firstFile.fileName,
    url: firstFile.url,
    category: target.category,
    folderSegments: revealFolderSegments,
    files: uploadedFiles,
  }
}

/** 单资源应用包装器，供普通图片/音频预览面板直接调用。 */
export async function applyMaterialResourceToWorkspace(params: {
  topCategoryId: string
  goods: MaterialGoods
  resource: MaterialResource
  onProgress?: (stage: MaterialUploadProgressStage, percent: number) => void
}): Promise<MaterialApplyResult> {
  return await applyMaterialResourcesToWorkspace({
    topCategoryId: params.topCategoryId,
    goods: params.goods,
    resources: [params.resource],
    onProgress: params.onProgress,
  })
}

type LoadedCompositeImage = {
  resource: MaterialResource
  blob: Blob
  url: string
  image: HTMLImageElement
}

async function loadCompositeImage(resource: MaterialResource, blob: Blob): Promise<LoadedCompositeImage> {
  const url = URL.createObjectURL(blob)
  const image = new Image()
  image.decoding = 'async'
  image.src = url
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve()
    image.onerror = () => reject(new Error('素材图层加载失败'))
  })
  return { resource, blob, url, image }
}

async function composeDollImage(resources: MaterialResource[]): Promise<Blob> {
  const loadedLayers: LoadedCompositeImage[] = []
  try {
    for (const resource of resources) {
      const blob = await downloadResourceBlob(resource)
      loadedLayers.push(await loadCompositeImage(resource, blob))
    }
    if (!loadedLayers.length) {
      throw new Error('当前没有可合成的素材图层')
    }
    const baseLayer = loadedLayers.find((item) => item.resource.extra.includes('身体\\')) ?? loadedLayers[0]!
    const width = baseLayer.image.naturalWidth || 0
    const height = baseLayer.image.naturalHeight || 0
    if (width <= 0 || height <= 0) {
      throw new Error('素材图层尺寸无效，无法合成')
    }
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('浏览器不支持素材合成画布')
    }
    for (const layer of loadedLayers) {
      ctx.drawImage(layer.image, 0, 0)
    }
    const composedBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob)
        else reject(new Error('素材合成失败，未生成图片'))
      }, 'image/png')
    })
    return composedBlob
  } finally {
    for (const layer of loadedLayers) {
      URL.revokeObjectURL(layer.url)
    }
  }
}

function buildDollCompositeFileName(goods: MaterialGoods, size: string, fallbackLabel = '素材'): string {
  const baseName = goods.name.trim().replace(/[\\/:*?"<>|]/g, '_') || fallbackLabel
  const normalizedSize = size.trim() || '540'
  return `${baseName}_${normalizedSize}_合成.png`
}

/** 将当前换装选择先合成为单张图片，再上传并写入虚拟资源树（立绘 / 背景 / CG）。 */
export async function applyDollCompositeToWorkspace(params: {
  topCategoryId: string
  goods: MaterialGoods
  resources: MaterialResource[]
  size: string
  onProgress?: (stage: MaterialUploadProgressStage, percent: number) => void
}): Promise<MaterialApplyResult> {
  const resources = uniqueMaterialResources(params.resources)
  if (!resources.length) {
    throw new Error('当前没有可应用的素材资源')
  }
  for (const resource of resources) {
    if (!resource.resmd5?.trim()) {
      throw new Error('当前素材缺少资源标识，无法应用')
    }
  }

  const { auth, workspace, project, guid, target } = await resolveApplyContext(params.topCategoryId)
  const version = String(project.projectHeader.Version || project.projectConstant.gameVersion || 1)
  const signature = buildDollCompositeSignature({
    goodsId: params.goods.id,
    size: params.size,
    resources,
  })
  let nextRoot = workspace.virtualResourceRoots[target.category]
  const existing = signature ? findMaterialResourceMappingByCompositeSignature(nextRoot, signature) : null
  if (existing) {
    workspace.revealResourceBrowserFolder(target.category, existing.folderSegments)
    return {
      fileName: existing.node.name,
      url: existing.node.links[0] ?? '',
      category: target.category,
      folderSegments: existing.folderSegments,
      files: [{ fileName: existing.node.name, url: existing.node.links[0] ?? '' }],
    }
  }

  const compositeLabel = target.folderSegments[0] || '素材'
  const composedBlob = await composeDollImage(resources)
  const localPreviewUrl = URL.createObjectURL(composedBlob)
  const composedFile = new File([composedBlob], buildDollCompositeFileName(params.goods, params.size, compositeLabel), {
    type: 'image/png',
  })
  const objectName = buildProjectResourceObjectName({
    guid,
    category: target.category,
    folderSegments: target.folderSegments,
    fileName: composedFile.name,
  })
  const uploaded = await uploadMaterialFile({
    file: composedFile,
    guid,
    sessionId: auth.sessionId,
    version,
    objectName,
    resultFileName: composedFile.name,
    onProgress: params.onProgress,
  })
  const mapped = appendMaterialResourceMappingEntry({
    root: nextRoot,
    category: target.category,
    folderSegments: target.folderSegments,
    fileName: uploaded.fileName,
    url: uploaded.url,
    md5: uploaded.md5,
    size: uploaded.size,
    extraLinks: signature
      ? {
          [MATERIAL_COMPOSITE_SIGNATURE_LINK_INDEX]: signature,
        }
      : undefined,
  })
  nextRoot = mapped.root
  workspace.setVirtualResourceRoot(target.category, nextRoot)
  await workspace.uploadVirtualResourcesToCloud()
  workspace.setResourcePreviewOverride(uploaded.url, localPreviewUrl)
  workspace.revealResourceBrowserFolder(target.category, target.folderSegments)
  return {
    fileName: mapped.fileName,
    url: uploaded.url,
    category: target.category,
    folderSegments: target.folderSegments,
    files: [{ fileName: mapped.fileName, url: uploaded.url }],
  }
}
